import React, { useRef, useState } from "react";
import { RefreshCw } from "lucide-react";
import { cn } from "../../lib/utils";

interface PullToRefreshProps {
  onRefresh: () => Promise<unknown>;
  onScroll?: (scrollTop: number) => void;
  className?: string;
  children: React.ReactNode;
}

const TRIGGER_DISTANCE = 72;
const MAX_DISTANCE = 112;
const REFRESHING_OFFSET = 56;
const INDICATOR_SIZE = 40;

/**
 * Scroll container with native-app style pull-to-refresh. Pull distance is
 * applied straight to the DOM so dragging never re-renders the children.
 */
const PullToRefresh: React.FC<PullToRefreshProps> = ({
  onRefresh,
  onScroll,
  className,
  children,
}) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const indicatorRef = useRef<HTMLDivElement>(null);
  const startYRef = useRef<number | null>(null);
  const distanceRef = useRef(0);
  const passedTriggerRef = useRef(false);
  const [isRefreshing, setIsRefreshing] = useState(false);

  const applyDistance = (distance: number, animate: boolean) => {
    const content = contentRef.current;
    const indicator = indicatorRef.current;
    if (!content || !indicator) return;
    const transition = animate ? "transform 0.3s ease, opacity 0.3s ease" : "none";
    content.style.transition = transition;
    content.style.transform = distance > 0 ? `translate3d(0, ${distance}px, 0)` : "";
    indicator.style.transition = transition;
    indicator.style.opacity = String(Math.min(distance / TRIGGER_DISTANCE, 1));
    indicator.style.transform = `translate3d(-50%, ${distance - INDICATOR_SIZE}px, 0) rotate(${distance * 3}deg)`;
  };

  const handleTouchStart = (event: React.TouchEvent) => {
    if (isRefreshing) return;
    startYRef.current =
      (scrollRef.current?.scrollTop ?? 0) <= 0 ? event.touches[0].clientY : null;
    passedTriggerRef.current = false;
  };

  const handleTouchMove = (event: React.TouchEvent) => {
    if (startYRef.current === null) return;
    if ((scrollRef.current?.scrollTop ?? 0) > 0) {
      startYRef.current = null;
      return;
    }
    const delta = event.touches[0].clientY - startYRef.current;
    const distance = delta > 0 ? Math.min(MAX_DISTANCE, delta * 0.45) : 0;
    distanceRef.current = distance;
    applyDistance(distance, false);

    if (!passedTriggerRef.current && distance >= TRIGGER_DISTANCE) {
      passedTriggerRef.current = true;
      navigator.vibrate?.(8);
    }
  };

  const handleTouchEnd = async () => {
    if (startYRef.current === null) return;
    startYRef.current = null;
    const distance = distanceRef.current;
    distanceRef.current = 0;

    if (distance < TRIGGER_DISTANCE) {
      applyDistance(0, true);
      return;
    }

    setIsRefreshing(true);
    applyDistance(REFRESHING_OFFSET, true);
    try {
      await onRefresh();
    } finally {
      setIsRefreshing(false);
      applyDistance(0, true);
    }
  };

  return (
    <div className={cn("relative h-full overflow-hidden", className)}>
      <div
        ref={indicatorRef}
        className="pointer-events-none absolute left-1/2 top-2 z-20 flex items-center justify-center rounded-full bg-white shadow-md ring-1 ring-gray-100"
        style={{
          width: INDICATOR_SIZE,
          height: INDICATOR_SIZE,
          opacity: 0,
          transform: `translate3d(-50%, ${-INDICATOR_SIZE}px, 0)`,
        }}
        aria-hidden="true"
      >
        <RefreshCw
          className={cn("h-5 w-5 text-primary-600", isRefreshing && "animate-spin")}
        />
      </div>
      <div
        ref={scrollRef}
        onScroll={(event) => onScroll?.(event.currentTarget.scrollTop)}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        onTouchCancel={handleTouchEnd}
        className="h-full overflow-y-auto overscroll-y-none"
      >
        <div ref={contentRef}>{children}</div>
      </div>
      <span className="sr-only" role="status" aria-live="polite">
        {isRefreshing ? "Refreshing" : ""}
      </span>
    </div>
  );
};

export default PullToRefresh;
